import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Card,
  Space,
  Spin,
  Row,
  Col,
  ConfigProvider,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../../hooks/useAuth";
import { useTheme } from "../../../context/ThemeContext";
import { getAntdThemeConfig } from "../../../utils/antdTheme";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTrendingItems } from "./store/trendingItemsSlice";
import DateRangePicker from "../../../commonComponents/DateRangePicker";
import StoresDropDown from "../StoresDropDown";
import CustomSelector from "../../../commonComponents/Selector";
import TrendingItemsHeader from "./TrendingItemsHeader";
import TrendingItemsSummary from "./TrendingItemsSummary";
import TrendingItemCard from "./TrendingItemCard";
import TrendingItemsTable from "./TrendingItemsTable";
import {
  DateRange,
  TrendDirection,
} from "./types";
import { trendDirectionOptions, minGrowthRateOptions } from "./constants";
import {
  MainContainer,
  FilterCard,
  FilterGrid,
  FilterLabel,
  StyledTabs,
  TabContent,
  TabHeader,
  PeriodCard,
  PeriodGrid,
  PeriodItem,
  PeriodLabel,
  PeriodValue,
  CardsContainer,
  FullWidthSpace,
  FilterTitle,
  MaxWidthInput,
  NoMarginFilterLabel,
  LoadingContainer,
  TabTitle,
  PeriodTitle,
  IconWrapper,
} from "./styles";

const { Text } = Typography;

// Main Trending Items Component
const TrendingItems: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();

  // Redux state
  const { data: trendingItemsData, loading: isLoading } = useSelector(
    (state: RootState) => state.trendingItems
  );

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment().subtract(30, "days"),
    endDate: moment(),
  });
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [trendDirection, setTrendDirection] = useState<TrendDirection>("all");
  const [minGrowthRate, setMinGrowthRate] = useState<number>(5);

  const merchant = user as { id?: string; role?: string } | null;
  const storeId = selectedStore || (user as { storeId?: string })?.storeId || null;

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  useEffect(() => {
    if (merchant?.id) {
      dispatch(
        fetchTrendingItems({
          startDate: moment(selectedDateRange.startDate).format("YYYY-MM-DD"),
          endDate: moment(selectedDateRange.endDate).format("YYYY-MM-DD"),
          merchantId: merchant.id,
          store: storeId,
          categoryId: selectedCategory || undefined,
        })
      );
    }
  }, [
    dispatch,
    merchant?.id,
    selectedDateRange,
    selectedStore,
    selectedCategory,
    storeId,
  ]);

  const handleDateRangeChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
  };

  const handleStoreChange = (store: { id: string }) => {
    setSelectedStore(store.id || null);
  };

  const handleCategoryChange = (value: string | number) => {
    setSelectedCategory(String(value));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTrendDirectionChange = (value: string | number) => {
    setTrendDirection(value as TrendDirection);
  };

  const handleMinGrowthRateChange = (value: string | number) => {
    setMinGrowthRate(Number(value));
  };

  const filteredItems = useMemo(() => {
    if (!trendingItemsData?.items) return [];

    return trendingItemsData.items.filter((item) => {
      const matchesSearch =
        item.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTrend = trendDirection === "all" || item.trend === trendDirection;

      const matchesGrowth = Math.abs(item.volumeGrowth) >= minGrowthRate;

      return matchesSearch && matchesTrend && matchesGrowth;
    });
  }, [trendingItemsData?.items, searchTerm, trendDirection, minGrowthRate]);

  const trendingUpItems = useMemo(
    () => filteredItems.filter((item) => item.trend === "up"),
    [filteredItems]
  );
  const trendingDownItems = useMemo(
    () => filteredItems.filter((item) => item.trend === "down"),
    [filteredItems]
  );
  const stableItems = useMemo(
    () => filteredItems.filter((item) => item.trend === "stable"),
    [filteredItems]
  );

  const isMobile = window.innerWidth < 768;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <MainContainer>
        <FullWidthSpace direction="vertical" size="large">
          <TrendingItemsHeader />
          {/* Filters */}
          <FilterCard>
            <FullWidthSpace direction="vertical" size="middle">
              <FilterTitle level={4}>
                Filters & Settings
              </FilterTitle>

              <FilterGrid>
                <div>
                  <FilterLabel>Date Range</FilterLabel>
                  <DateRangePicker
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={handleDateRangeChange}
                  />
                </div>

                <div>
                  <FilterLabel>Store</FilterLabel>
                  <StoresDropDown
                    onOptionSelect={(store: { id: string }) => handleStoreChange(store)}
                    merchantId={merchant?.id}
                    role={merchant?.role || user?.role}
                  />
                </div>

                <div>
                  <FilterLabel>Category</FilterLabel>
                  <CustomSelector
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    options={[{ value: "", label: "All Categories" }]}
                    placeholder="All Categories"
                    width="100%"
                  />
                </div>

                <div>
                  <FilterLabel>Trend Direction</FilterLabel>
                  <CustomSelector
                    value={trendDirection}
                    onChange={handleTrendDirectionChange}
                    options={trendDirectionOptions}
                    width="100%"
                  />
                </div>
              </FilterGrid>

              <Row gutter={16} align="middle">
                <Col>
                  <MaxWidthInput
                    placeholder="Search items or categories"
                    prefix={<IconWrapper color="#8c8c8c"><FontAwesomeIcon icon={faSearch} /></IconWrapper>}
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </Col>
                <Col>
                  <Space>
                    <NoMarginFilterLabel>Min Growth Rate</NoMarginFilterLabel>
                    <CustomSelector
                      value={minGrowthRate}
                      onChange={handleMinGrowthRateChange}
                      options={minGrowthRateOptions}
                      width={120}
                    />
                  </Space>
                </Col>
              </Row>
            </FullWidthSpace>
          </FilterCard>

          {trendingItemsData?.summary && (
            <TrendingItemsSummary summary={trendingItemsData.summary} />
          )}

          {isLoading ? (
            <Card>
              <LoadingContainer>
                <Spin size="large" />
              </LoadingContainer>
            </Card>
          ) : (
            <StyledTabs
              defaultActiveKey="all"
              items={[
                {
                  key: "all",
                  label: `All Items (${filteredItems.length})`,
                  children: (
                    <TabContent>
                      <TabHeader>
                        <TabTitle level={4}>
                          All Trending Items
                        </TabTitle>
                        <Text type="secondary">
                          Total: {trendingItemsData?.totalTrendingItems || 0} items
                        </Text>
                      </TabHeader>

                      {isMobile ? (
                        <CardsContainer>
                          {filteredItems.map((item, index) => (
                            <TrendingItemCard
                              key={`${item.item}-${index}`}
                              item={item}
                              index={index}
                            />
                          ))}
                        </CardsContainer>
                      ) : (
                        <TrendingItemsTable items={filteredItems} isLoading={isLoading} />
                      )}
                    </TabContent>
                  ),
                },
                {
                  key: "up",
                  label: `Trending Up (${trendingUpItems.length})`,
                  children: (
                    <TabContent>
                      <TabHeader>
                        <TabTitle level={4}>
                          Trending Up Items
                        </TabTitle>
                        <Text type="secondary">{trendingUpItems.length} items</Text>
                      </TabHeader>

                      {isMobile ? (
                        <CardsContainer>
                          {trendingUpItems.map((item, index) => (
                            <TrendingItemCard
                              key={`${item.item}-${index}`}
                              item={item}
                              index={index}
                            />
                          ))}
                        </CardsContainer>
                      ) : (
                        <TrendingItemsTable items={trendingUpItems} isLoading={isLoading} />
                      )}
                    </TabContent>
                  ),
                },
                {
                  key: "down",
                  label: `Trending Down (${trendingDownItems.length})`,
                  children: (
                    <TabContent>
                      <TabHeader>
                        <TabTitle level={4}>
                          Trending Down Items
                        </TabTitle>
                        <Text type="secondary">{trendingDownItems.length} items</Text>
                      </TabHeader>

                      {isMobile ? (
                        <CardsContainer>
                          {trendingDownItems.map((item, index) => (
                            <TrendingItemCard
                              key={`${item.item}-${index}`}
                              item={item}
                              index={index}
                            />
                          ))}
                        </CardsContainer>
                      ) : (
                        <TrendingItemsTable items={trendingDownItems} isLoading={isLoading} />
                      )}
                    </TabContent>
                  ),
                },
                {
                  key: "stable",
                  label: `Stable (${stableItems.length})`,
                  children: (
                    <TabContent>
                      <TabHeader>
                        <TabTitle level={4}>
                          Stable Items
                        </TabTitle>
                        <Text type="secondary">{stableItems.length} items</Text>
                      </TabHeader>

                      {isMobile ? (
                        <CardsContainer>
                          {stableItems.map((item, index) => (
                            <TrendingItemCard
                              key={`${item.item}-${index}`}
                              item={item}
                              index={index}
                            />
                          ))}
                        </CardsContainer>
                      ) : (
                        <TrendingItemsTable items={stableItems} isLoading={isLoading} />
                      )}
                    </TabContent>
                  ),
                },
              ]}
            />
          )}

          {trendingItemsData?.period && (
            <PeriodCard>
              <FullWidthSpace direction="vertical" size="middle">
                <PeriodTitle level={4}>
                  Analysis Period
                </PeriodTitle>
                <PeriodGrid>
                  <PeriodItem>
                    <PeriodLabel>Current Period</PeriodLabel>
                    <PeriodValue>
                      {moment(trendingItemsData.period.current.startDate).format("MMM DD, YYYY")} -{" "}
                      {moment(trendingItemsData.period.current.endDate).format("MMM DD, YYYY")}
                    </PeriodValue>
                  </PeriodItem>
                  <PeriodItem>
                    <PeriodLabel>Previous Period</PeriodLabel>
                    <PeriodValue>
                      {moment(trendingItemsData.period.previous.startDate).format("MMM DD, YYYY")} -{" "}
                      {moment(trendingItemsData.period.previous.endDate).format("MMM DD, YYYY")}
                    </PeriodValue>
                  </PeriodItem>
                </PeriodGrid>
              </FullWidthSpace>
            </PeriodCard>
          )}
        </FullWidthSpace>
      </MainContainer>
      </ConfigProvider>
    </StyledThemeProvider>
  );
};

export default TrendingItems;
